import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MainHeader from "../components/MainHeader";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const EMPTY = { name: "", bio: "", portrait_url: "", accent_color: "#C84B31", location: "" };

export default function AdminFilmmakers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => { document.body.classList.remove("theme-mini"); }, []);
  const load = () => api.get("/filmmakers").then(({ data }) => setItems(data));
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/filmmakers", form);
      else await api.patch(`/filmmakers/${editing}`, form);
      toast.success("Saved.");
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Remove this filmmaker?")) return;
    try { await api.delete(`/filmmakers/${slug}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-filmmakers-page" className="min-h-screen bg-[#F4F2EC]">
      <MainHeader />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">Projection booth</p>
            <h1 className="font-outfit font-semibold text-4xl text-gs-ink mt-2">Filmmakers</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-3 font-mono text-[10px] uppercase tracking-widest">
              <Link to="/admin" className="text-gs-dust hover:text-gs-ink">Films</Link>
              <Link to="/admin/sales" className="text-gs-dust hover:text-gs-ink">Sales</Link>
              <span className="text-gs-ember">Filmmakers</span>
            </div>
            <button onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="add-filmmaker-btn" className="bg-gs-ink text-white rounded-md px-4 py-2 text-sm inline-flex items-center gap-2">
              <Plus size={14} /> Add
            </button>
          </div>
        </div>

        {editing && (
          <form onSubmit={save} data-testid="filmmaker-form" className="bg-white border border-black/5 rounded-xl p-6 mb-10 grid md:grid-cols-2 gap-5 shadow-sm">
            <Field label="Name" name="name" form={form} setForm={setForm} required />
            <Field label="Location" name="location" form={form} setForm={setForm} />
            <Field label="Portrait URL" name="portrait_url" form={form} setForm={setForm} />
            <Field label="Accent color (hex)" name="accent_color" form={form} setForm={setForm} />
            <div className="md:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-2">Bio</label>
              <textarea data-testid="filmmaker-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="w-full bg-white border border-gs-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gs-ember" />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <button type="submit" data-testid="save-filmmaker-btn" className="bg-gs-ink text-gs-cream rounded-none px-5 py-2 text-xs font-mono uppercase tracking-[0.25em] border border-gs-ember/50 hover:bg-gs-ember hover:text-gs-ink inline-flex items-center gap-2"><Save size={14} /> Save</button>
              <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="text-sm text-gs-dust">Cancel</button>
            </div>
          </form>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {items.map((fm, i) => (
            <div key={fm.id} data-testid={`filmmaker-card-${i}`} className="bg-white border border-black/5 rounded-xl flex overflow-hidden shadow-sm">
              <div className="w-28 aspect-[3/4] bg-gs-ink shrink-0">
                {fm.portrait_url && <img src={fm.portrait_url} alt={fm.name} className="w-full h-full object-cover" />}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <p className="font-outfit font-semibold text-xl text-gs-ink">{fm.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust">{fm.location || "—"}</p>
                <p className="font-sans text-xs text-gs-ink/70 mt-2 line-clamp-3">{fm.bio}</p>
                <div className="flex items-center gap-3 mt-auto pt-3 text-xs">
                  <Link to={`/f/${fm.slug}`} className="text-gs-ink hover:text-gs-ember">View page</Link>
                  <a href={`/?as=${fm.slug}`} data-testid={`preview-subdomain-${i}`} className="text-gs-ink hover:text-gs-ember" title="Open as if visiting their subdomain">
                    Preview subdomain
                  </a>
                  <button onClick={() => { setForm({ name: fm.name, bio: fm.bio, portrait_url: fm.portrait_url, accent_color: fm.accent_color, location: fm.location || "" }); setEditing(fm.slug); }} className="text-gs-ink hover:text-gs-ember">Edit</button>
                  <button onClick={() => remove(fm.slug)} className="text-gs-dust hover:text-red-700 ml-auto inline-flex items-center gap-1">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, form, setForm, required }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-gs-dust mb-2">{label}</label>
      <input data-testid={`fm-field-${name}`} value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })} required={required} className="w-full bg-white border border-gs-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gs-ember" />
    </div>
  );
}
